<template>
  <div>
    <!-- 投票态：百分比 + 双色按钮 -->
    <div v-if="!settled" :class="isPC ? 'w-[490px] mx-auto' : ''">
      <template v-if="showPercent">
        <!-- PC：百分比与进度条同行 -->
        <div v-if="isPC" class="mb-[20px] flex items-center gap-[8px]">
          <span class="text-[15px] font-medium leading-none shrink-0" :style="{ color: EVENT_LEFT_COLOR }">{{ leftPercent }}%</span>
          <div class="h-[6px] flex-1 overflow-hidden rounded-[3px]" :style="{ background: progressGradient }"></div>
          <span class="text-[15px] font-medium leading-none shrink-0" :style="{ color: EVENT_RIGHT_COLOR }">{{ rightPercent }}%</span>
        </div>
        <!-- 移动端：百分比在上，进度条在下 -->
        <template v-else>
          <div class="mb-[8px] flex items-end justify-between px-[4px]">
            <span class="text-[24px] font-medium leading-none" :style="{ color: EVENT_LEFT_COLOR }">{{ leftPercent }}%</span>
            <span class="text-[24px] font-medium leading-none" :style="{ color: EVENT_RIGHT_COLOR }">{{ rightPercent }}%</span>
          </div>
          <div class="mb-[20px] h-[6px] w-full overflow-hidden rounded-[3px]" :style="{ background: progressGradient }"></div>
        </template>
      </template>

      <div
        class="flex w-full overflow-hidden"
        :style="{ borderRadius: '39px', background: btnGradient }"
      >
        <button
          type="button"
          class="flex flex-1 items-center justify-center overflow-hidden border-none bg-transparent font-medium text-white outline-none"
          :class="isPC ? 'py-[16px] text-[24px] leading-[28px]' : 'pt-[22px] pb-[24px] text-[28px] leading-[32px]'"
          @click="leftOption && emit('select', leftOption)"
        >
          <img v-if="leftSelected" src="../images/icon-check.png" class="mr-[8px] h-[23px] w-[28px] shrink-0" />
          <span class="truncate">{{ leftOption ? $label(leftOption.label) : '' }}</span>
        </button>
        <button
          type="button"
          class="flex flex-1 items-center justify-center overflow-hidden border-none bg-transparent font-medium text-white outline-none"
          :class="isPC ? 'py-[16px] text-[24px] leading-[28px]' : 'pt-[22px] pb-[24px] text-[28px] leading-[32px]'"
          @click="rightOption && emit('select', rightOption)"
        >
          <span class="truncate">{{ rightOption ? $label(rightOption.label) : '' }}</span>
          <img v-if="rightSelected" src="../images/icon-check.png" class="ml-[8px] h-[23px] w-[28px] shrink-0" />
        </button>
      </div>
    </div>

    <!-- 结算态：结果对比卡片 -->
    <div v-else :class="isPC ? 'flex gap-[15px] justify-center' : 'flex gap-[8px]'">
      <!-- 左侧：本次结果 -->
      <div
        class="rounded-[16px] text-white"
        :class="isPC ? 'pt-[16px] pl-[20px] pr-[22px] pb-[22px] w-[233px] flex-none' : 'flex-1 px-[22px] py-[26px]'"
        :style="{ backgroundColor: winnerTagBg }"
      >
        <div :class="isPC ? 'mb-[30px]' : 'mb-[20px]'">
          <span
            class="inline-block rounded-[4px] px-[8px] py-[2px]"
            :class="isPC ? 'text-[22px] leading-[25px]' : 'text-[24px] leading-[28px]'"
          >
            {{ $t('actualResult') }}
          </span>
        </div>
        <div
          class="flex w-full rounded-[12px] text-[40px] leading-[46px]"
        >
          {{ winnerLabel }}
        </div>
      </div>
      <!-- 右侧：我的预测 -->
      <div
        class="rounded-[16px] relative text-white border-solid"
        :class="isPC ? 'pt-[16px] pl-[20px] pr-[22px] pb-[22px] w-[233px] flex-none' : 'flex-1 px-[22px] pt-[26px] pb-[22px]'"
        :style="{ backgroundColor: myVoteCardBg, borderWidth: isCorrect ? '1px' : '0', borderColor: isCorrect ? winnerColor : 'transparent' }"
      >
        <div :class="isPC ? 'mb-[30px]' : 'mb-[20px]'">
          <span
            class="inline-block rounded-[4px] px-[8px] py-[2px] text-[#7a7a7a]"
            :class="isPC ? 'text-[22px] leading-[25px]' : 'text-[24px] leading-[28px]'"
          >
            {{ pick($t, 'myAnalysis', 'myPrediction') }}
          </span>
        </div>
        <!-- 已参与且猜中 -->
        <div
          v-if="myVoteOption && isCorrect"
          class=" flex text-[40px] leading-[46px]"
          :style="{ color: winnerColor }"
        >
          {{ $label(myVoteOption.label) }}
        </div>
        <!-- 未猜中 / 未参与 -->
        <div
          v-else
          class="flex text-[40px] leading-[46px]"
          :style="{ color: myVoteOption ? '#121212' : '#C3C3C3' }"
        >
          {{ myVoteOption ? $label(myVoteOption.label) : '--' }}
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed } from 'vue-demi';
import { use as fegoUse } from '@fegobull/webapi';
import { useLocaleString, useFegoPageInfo } from '@ark/web-shared/use';
import type { PredictOption } from '../../shared/types';
import { EVENT_LEFT_COLOR, EVENT_RIGHT_COLOR, GAP_COLOR, MUTED_COLOR } from '../../shared/constants';
import { useWording } from '../hooks/useWording';
import locales from '../locales';
const __sfc_main = {};
__sfc_main.props = {
  options: {
    key: "options",
    required: false,
    type: Array,
    default: () => []
  },
  selectedCode: {
    key: "selectedCode",
    required: false,
    type: [String, null],
    default: null
  },
  settled: {
    key: "settled",
    required: false,
    type: Boolean,
    default: false
  },
  winnerCode: {
    key: "winnerCode",
    required: false,
    type: [String, null],
    default: null
  },
  showPercent: {
    key: "showPercent",
    required: false,
    type: Boolean,
    default: true
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const {
    useI18n
  } = fegoUse;
  const $t = useI18n(locales);
  const $label = useLocaleString();
  const {
    pick
  } = useWording();
  const {
    isPC
  } = useFegoPageInfo();
  const ANGLE = '102deg';
  const props = __props;
  const emit = __ctx.emit;

  // ---- 投票态逻辑 ----
  const leftOption = computed<PredictOption | undefined>(() => props.options[0]);
  const rightOption = computed<PredictOption | undefined>(() => props.options[1]);
  const hasSelection = computed(() => props.selectedCode != null);
  const leftSelected = computed(() => hasSelection.value && leftOption.value && leftOption.value.code === props.selectedCode);
  const rightSelected = computed(() => hasSelection.value && rightOption.value && rightOption.value.code === props.selectedCode);
  const effectiveLeftColor = computed(() => rightSelected.value ? MUTED_COLOR : EVENT_LEFT_COLOR);
  const effectiveRightColor = computed(() => leftSelected.value ? MUTED_COLOR : EVENT_RIGHT_COLOR);
  const rawLeftPercent = computed(() => leftOption.value && leftOption.value.percent != null ? leftOption.value.percent : 0);
  const rawRightPercent = computed(() => rightOption.value && rightOption.value.percent != null ? rightOption.value.percent : 0);
  const leftPercent = computed(() => rawLeftPercent.value);
  const rightPercent = computed(() => rawRightPercent.value);
  const progressGradient = computed(() => {
    const lp = rawLeftPercent.value === 0 && rawRightPercent.value === 0 ? 50 : leftPercent.value;
    return `linear-gradient(${ANGLE}, ${EVENT_LEFT_COLOR} 0%, ${EVENT_LEFT_COLOR} calc(${lp}% - 1px), ${GAP_COLOR} calc(${lp}% - 1px), ${GAP_COLOR} calc(${lp}% + 1px), ${EVENT_RIGHT_COLOR} calc(${lp}% + 1px), ${EVENT_RIGHT_COLOR} 100%)`;
  });
  const btnGradient = computed(() => {
    const lc = effectiveLeftColor.value;
    const rc = effectiveRightColor.value;
    return `linear-gradient(${ANGLE}, ${lc} 0%, ${lc} calc(50% - 2px), ${GAP_COLOR} calc(50% - 2px), ${GAP_COLOR} calc(50% + 2px), ${rc} calc(50% + 2px), ${rc} 100%)`;
  });

  // ---- 结算态逻辑 ----
  const winnerOption = computed(() => {
    if (!props.winnerCode) return undefined;
    return props.options.find(o => o.code === props.winnerCode);
  });
  const myVoteOption = computed(() => {
    if (!props.selectedCode) return undefined;
    return props.options.find(o => o.code === props.selectedCode);
  });
  const winnerLabel = computed(() => winnerOption.value ? $label(winnerOption.value.label) : '');
  const isCorrect = computed(() => props.winnerCode != null && props.selectedCode != null && props.winnerCode === props.selectedCode);
  const winnerColor = computed(() => {
    if (!winnerOption.value || !leftOption.value) return EVENT_LEFT_COLOR;
    return winnerOption.value.code === leftOption.value.code ? EVENT_LEFT_COLOR : EVENT_RIGHT_COLOR;
  });
  const winnerTagBg = computed(() => winnerColor.value === EVENT_LEFT_COLOR ? EVENT_LEFT_COLOR : EVENT_RIGHT_COLOR);
  const myVoteCardBg = computed(() => {
    if (!isCorrect.value) return isPC ? '#F6F7F9' : '#fff';
    return winnerColor.value === EVENT_LEFT_COLOR ? '#F7FAFF' : '#FFF9F5';
  });
  return {
    EVENT_LEFT_COLOR,
    EVENT_RIGHT_COLOR,
    $t,
    $label,
    pick,
    isPC,
    emit,
    leftOption,
    rightOption,
    leftSelected,
    rightSelected,
    leftPercent,
    rightPercent,
    progressGradient,
    btnGradient,
    myVoteOption,
    winnerLabel,
    isCorrect,
    winnerColor,
    winnerTagBg,
    myVoteCardBg
  };
};
export default __sfc_main;
</script>
